import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../lib/supabase';

interface ContextMenuItem {
  label: string;
  action: () => void;
  icon?: React.ReactElement;
  separator?: boolean;
}

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  items: ContextMenuItem[];
  visible: boolean;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, onClose, items, visible }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-48"
      style={{ left: x, top: y }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {item.separator && <hr className="my-1 border-gray-200" />}
          <button
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => {
              item.action();
              onClose();
            }}
          >
            {item.icon && <span className="text-gray-400">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

interface AITransformModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransform: (request: string) => void;
  isLoading: boolean;
}

const AITransformModal: React.FC<AITransformModalProps> = ({ isOpen, onClose, onTransform, isLoading }) => {
  const [request, setRequest] = useState('');

  const handleSubmit = () => {
    if (request.trim() && !isLoading) {
      onTransform(request.trim());
      setRequest('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-md w-full mx-0 sm:mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Transform with AI</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="space-y-3">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mt-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6 mt-2"></div>
              </div>
              <div className="text-sm text-gray-500 flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Generating suggestions...</span>
              </div>
            </div>
          ) : (
            <>
              <label className="block text-sm text-gray-600 mb-3">
                Describe how you'd like to improve your prompt
              </label>
              <textarea
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                placeholder="e.g., Make it more professional, add examples, improve clarity..."
                autoFocus
              />
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleSubmit}
                  disabled={!request.trim()}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Generate
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface PromptSuggestion {
  originalPrompt: string;
  suggestedPrompt: string;
  transformationRequest: string;
  selectionStart?: number;
  selectionEnd?: number;
  selectedText?: string;
}

interface SuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
  onRefine: (refinementRequest: string) => void;
  suggestion: PromptSuggestion | null;
  isLoading?: boolean;
}

const SuggestionModal: React.FC<SuggestionModalProps> = ({ 
  isOpen, 
  onClose, 
  onAccept, 
  onReject, 
  onRefine,
  suggestion,
  isLoading = false
}) => {
  const [showRefineInput, setShowRefineInput] = useState(false);
  const [refinementRequest, setRefinementRequest] = useState('');
  if (!isOpen || !suggestion) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            <h3 className="text-lg font-medium text-gray-900">AI Suggestion</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Your request:</strong> {suggestion.transformationRequest}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                <span>
                  {suggestion.selectedText ? 'Selected Text' : 'Current Prompt'}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">Original</span>
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 min-h-48">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {suggestion.selectedText || suggestion.originalPrompt || 'No prompt content'}
                </pre>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                <span>AI Suggestion</span>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                  {isLoading ? 'Refining...' : 'Improved'}
                </span>
              </h4>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 min-h-48 relative">
                {isLoading ? (
                  <div className="absolute inset-0 bg-green-50 bg-opacity-90 flex flex-col items-center justify-center rounded-lg">
                    <div className="space-y-3 w-full p-3">
                      <div className="animate-pulse">
                        <div className="h-4 bg-green-200 rounded w-3/4"></div>
                        <div className="h-4 bg-green-200 rounded w-1/2 mt-2"></div>
                        <div className="h-4 bg-green-200 rounded w-5/6 mt-2"></div>
                        <div className="h-4 bg-green-200 rounded w-2/3 mt-2"></div>
                      </div>
                      <div className="text-sm text-green-700 flex items-center justify-center space-x-2 mt-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        <span>Refining suggestion...</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {suggestion.suggestedPrompt}
                  </pre>
                )}
              </div>
            </div>
          </div>

          {showRefineInput && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="block text-sm font-medium text-blue-900 mb-2">
                How would you like to refine this suggestion?
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={refinementRequest}
                  onChange={(e) => setRefinementRequest(e.target.value)}
                  disabled={isLoading}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={isLoading ? "Refining..." : "e.g., make it shorter, add more examples, change tone..."}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && refinementRequest.trim() && !isLoading) {
                      onRefine(refinementRequest.trim());
                      setRefinementRequest('');
                      setShowRefineInput(false);
                    }
                  }}
                  autoFocus={!isLoading}
                />
                <button
                  onClick={() => {
                    if (refinementRequest.trim() && !isLoading) {
                      onRefine(refinementRequest.trim());
                      setRefinementRequest('');
                      setShowRefineInput(false);
                    }
                  }}
                  disabled={!refinementRequest.trim() || isLoading}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Applying...' : 'Apply'}
                </button>
                <button
                  onClick={() => {
                    if (!isLoading) {
                      setShowRefineInput(false);
                      setRefinementRequest('');
                    }
                  }}
                  disabled={isLoading}
                  className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
              <div>
                <p className="text-sm font-medium text-amber-800">AI-Generated Content</p>
                <p className="text-sm text-amber-700 mt-1">
                  Please review the suggested changes carefully. AI-generated content may contain inaccuracies or biases.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onReject}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            Keep Original
          </button>
          <div className="flex space-x-2">
            <button
              onClick={() => !isLoading && setShowRefineInput(!showRefineInput)}
              disabled={isLoading}
              className="px-4 py-2 text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Refine
            </button>
            <button
              onClick={onAccept}
              disabled={isLoading}
              className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Use Suggestion
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Spark Icon Component
interface SparkIconProps {
  position: { top: number; left: number };
  onClick: () => void;
  visible: boolean;
}

const SparkIcon: React.FC<SparkIconProps> = ({ position, onClick, visible }) => {
  if (!visible) return null;

  return createPortal(
    <div
      className="fixed z-50 w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center cursor-pointer hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 border border-white/20"
      style={{ 
        top: position.top, 
        left: position.left,
        transform: 'translateY(-50%)'
      }}
      onClick={onClick}
    >
      <svg className="w-4 h-4 text-white drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/>
      </svg>
    </div>,
    document.body
  );
};

// Action Popup Component  
interface ActionPopupProps {
  position: { top: number; left: number };
  hasSelection: boolean;
  onTransform: () => void;
  onAskQuestion: () => void;
  onClose: () => void;
  visible: boolean;
}

const ActionPopup: React.FC<ActionPopupProps> = ({ 
  position, 
  hasSelection, 
  onTransform, 
  onAskQuestion, 
  onClose, 
  visible 
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-44"
      style={{ 
        top: position.top + 10, 
        left: position.left + 35
      }}
    >
      {hasSelection ? (
        <>
          <button
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => {
              onAskQuestion();
              onClose();
            }}
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span>Ask a question</span>
          </button>
          <button
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => {
              onTransform();
              onClose();
            }}
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            <span>Transform with AI</span>
          </button>
        </>
      ) : (
        <button
          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          onClick={() => {
            onTransform();
            onClose();
          }}
        >
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          <span>Transform with AI</span>
        </button>
      )}
    </div>,
    document.body
  );
};

interface AIPromptAssistantProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
}

const AIPromptAssistant: React.FC<AIPromptAssistantProps> = ({
  value,
  onChange,
  placeholder,
  rows = 12,
  className = ""
}) => {
  const [contextMenu, setContextMenu] = useState<{x: number, y: number, visible: boolean}>({
    x: 0, y: 0, visible: false
  });
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState<PromptSuggestion | null>(null);
  const [selectionInfo, setSelectionInfo] = useState<{start: number, end: number, text: string}>({
    start: 0, end: 0, text: ''
  });
  const [showSparkIcon, setShowSparkIcon] = useState(false);
  const [sparkPosition, setSparkPosition] = useState<{top: number, left: number}>({ top: 0, left: 0 });
  const [sparkActionPopup, setSparkActionPopup] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate line position for spark icon placement (simplified)
  const getLinePosition = (textarea: HTMLTextAreaElement, cursorPos: number) => {
    const textBeforeCursor = value.substring(0, cursorPos);
    const lines = textBeforeCursor.split('\n');
    const currentLineIndex = lines.length - 1;
    
    // Get line height from computed styles
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight) || 20;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    
    const textareaRect = textarea.getBoundingClientRect();
    
    return {
      top: textareaRect.top + paddingTop + (currentLineIndex * lineHeight) + (lineHeight / 2) - textarea.scrollTop,
      left: textareaRect.left - 35 // Position relative to viewport
    };
  };

  // Handle cursor movement and selection changes
  const handleSelectionChange = () => {
    if (!textareaRef.current || !containerRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    setSelectionInfo({ start, end, text: selectedText });
    
    // Show spark icon if there's a selection or cursor is focused
    if (document.activeElement === textarea && (selectedText || start === end)) {
      const position = getLinePosition(textarea, selectedText ? start : start);
      setSparkPosition(position);
      setShowSparkIcon(true);
    } else {
      setShowSparkIcon(false);
    }
  };

  // Hide spark icon when textarea loses focus
  const handleBlur = () => {
    setTimeout(() => {
      if (!sparkActionPopup) {
        setShowSparkIcon(false);
      }
    }, 150); // Small delay to allow clicking on spark icon
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Capture current selection
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      
      setSelectionInfo({
        start,
        end,
        text: selectedText
      });
    }
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true
    });
  };

  const handleClipboardAction = async (action: 'copy' | 'paste' | 'cut') => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    switch (action) {
      case 'copy':
        if (selectedText) {
          await navigator.clipboard.writeText(selectedText);
        } else {
          await navigator.clipboard.writeText(value);
        }
        break;
      case 'cut':
        if (selectedText) {
          await navigator.clipboard.writeText(selectedText);
          const newValue = value.substring(0, start) + value.substring(end);
          onChange(newValue);
        }
        break;
      case 'paste':
        try {
          const clipboardText = await navigator.clipboard.readText();
          const newValue = value.substring(0, start) + clipboardText + value.substring(end);
          onChange(newValue);
        } catch (err) {
          console.error('Failed to paste:', err);
        }
        break;
    }
  };

  const handleAITransform = async (request: string) => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User not authenticated.");
      }
      const token = session.access_token;
      const backendUrl = process.env.REACT_APP_AI_BACKEND_URL;
      if (!backendUrl) {
        throw new Error("AI_BACKEND_URL is not configured.");
      }

      let requestBody: any;
      const hasSelection = selectionInfo.text;

      if (hasSelection) {
        const textBefore = value.substring(Math.max(0, selectionInfo.start - 100), selectionInfo.start);
        const textAfter = value.substring(selectionInfo.end, Math.min(value.length, selectionInfo.end + 100));
        requestBody = {
          user_input: selectionInfo.text,
          transformation_request: request,
          transformation_type: "selected_text",
          text_before: textBefore,
          text_after: textAfter
        };
      } else {
        requestBody = {
          user_input: value,
          transformation_request: request,
          transformation_type: "full_text"
        };
      }
      
      const response = await fetch(`${backendUrl}/v1/utils/prompt_helper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `API request failed with status ${response.status}`);
      }

      const data = await response.json();
      const suggestedPrompt = data.rewritten_text;

      setCurrentSuggestion({
        originalPrompt: value,
        suggestedPrompt,
        transformationRequest: request,
        selectionStart: selectionInfo.start,
        selectionEnd: selectionInfo.end,
        selectedText: selectionInfo.text
      });
      setAiModalOpen(false);
      setSuggestionModalOpen(true);
    } catch (error) {
      console.error('AI transformation failed:', error);
      alert(`An error occurred during AI transformation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefine = async (refinementRequest: string) => {
    if (!currentSuggestion) return;
    
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User not authenticated.");
      }
      const token = session.access_token;
      const backendUrl = process.env.REACT_APP_AI_BACKEND_URL;
      if (!backendUrl) {
        throw new Error("AI_BACKEND_URL is not configured.");
      }

      const response = await fetch(`${backendUrl}/v1/utils/prompt_helper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_input: currentSuggestion.suggestedPrompt,
          transformation_request: refinementRequest
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `API request failed with status ${response.status}`);
      }

      const data = await response.json();
      const refinedSuggestion = data.rewritten_text;
      
      setCurrentSuggestion({
        ...currentSuggestion,
        suggestedPrompt: refinedSuggestion,
        transformationRequest: `${currentSuggestion.transformationRequest} → ${refinementRequest}`
      });
    } catch (error) {
      console.error('Refinement failed:', error);
      alert(`An error occurred during refinement: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const applySmartReplacement = (originalText: string, newText: string, selection: {start: number, end: number, text: string}): string => {
    // If no selection, replace entire text
    if (!selection.text) {
      return newText;
    }

    // Smart replacement: replace the selected portion with the new text
    const before = originalText.substring(0, selection.start);
    const after = originalText.substring(selection.end);
    
    // Try to detect if the new text is an enhancement vs a complete replacement
    const isEnhancement = newText.toLowerCase().includes(selection.text.toLowerCase());
    
    if (isEnhancement) {
      // If it's an enhancement, use the new text as-is
      return before + newText + after;
    } else {
      // If it's a replacement, substitute cleanly
      return before + newText + after;
    }
  };

  // Spark icon handlers
  const handleSparkIconClick = () => {
    // Update selection info when spark icon is clicked
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      
      setSelectionInfo({ start, end, text: selectedText });
      
      if (selectedText) {
        // Show action popup for selected text
        setSparkActionPopup(true);
      } else {
        // Direct transform for cursor-only (full prompt)
        setAiModalOpen(true);
      }
    }
  };

  const handleAskQuestion = () => {
    // TODO: Implement ask question functionality
    console.log('Ask question about:', selectionInfo.text);
    // For now, we'll show a placeholder
    alert('Ask a question functionality coming soon!');
  };

  const handleSparkTransform = () => {
    setAiModalOpen(true);
  };

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: 'Copy',
      action: () => handleClipboardAction('copy'),
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
      </svg>
    },
    {
      label: 'Paste',
      action: () => handleClipboardAction('paste'),
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
      </svg>
    },
    {
      label: 'Cut',
      action: () => handleClipboardAction('cut'),
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M15 14h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    },
    { separator: true, label: '', action: () => {} },
    {
      label: 'Transform with AI',
      action: () => setAiModalOpen(true),
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
      </svg>
    }
  ];

  return (
    <div ref={containerRef} className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onContextMenu={handleContextMenu}
        onSelect={handleSelectionChange}
        onKeyUp={handleSelectionChange}
        onMouseUp={handleSelectionChange}
        onFocus={handleSelectionChange}
        onBlur={handleBlur}
        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent resize-none text-sm ${className}`}
        rows={rows}
        placeholder={placeholder}
      />

      <SparkIcon
        position={sparkPosition}
        onClick={handleSparkIconClick}
        visible={showSparkIcon}
      />

      <ActionPopup
        position={sparkPosition}
        hasSelection={!!selectionInfo.text}
        onTransform={handleSparkTransform}
        onAskQuestion={handleAskQuestion}
        onClose={() => setSparkActionPopup(false)}
        visible={sparkActionPopup}
      />

      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        visible={contextMenu.visible}
        onClose={() => setContextMenu(prev => ({ ...prev, visible: false }))}
        items={contextMenuItems}
      />

      <AITransformModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        onTransform={handleAITransform}
        isLoading={isLoading}
      />

      <SuggestionModal
        isOpen={suggestionModalOpen}
        onClose={() => setSuggestionModalOpen(false)}
        onAccept={() => {
          if (currentSuggestion) {
            // Apply smart replacement based on whether text was selected
            const newValue = applySmartReplacement(
              currentSuggestion.originalPrompt,
              currentSuggestion.suggestedPrompt,
              {
                start: currentSuggestion.selectionStart || 0,
                end: currentSuggestion.selectionEnd || 0,
                text: currentSuggestion.selectedText || ''
              }
            );
            onChange(newValue);
          }
          setSuggestionModalOpen(false);
          setCurrentSuggestion(null);
        }}
        onReject={() => {
          setSuggestionModalOpen(false);
          setCurrentSuggestion(null);
        }}
        onRefine={handleRefine}
        suggestion={currentSuggestion}
        isLoading={isLoading}
      />
    </div>
  );
};

export default AIPromptAssistant;